import {
  AddIcon,
  Button,
  Chip,
  DeleteIcon,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  SearchInput,
  Table,
  type TableColumns,
} from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "urql";
import { getFragmentData, graphql } from "../../client/gql";
import { ClientFormModal } from "../../components/AuthClients/ClientFormModal/ClientFormModal";
import { useConfirm } from "../../hooks/useConfirm";
import { Page } from "../../components/Page/Page";
import { PageToolbar } from "../../components/PageToolbar/PageToolbar";
import { showSuccessToast, showUserErrorToast } from "../../utils/toast";

// Colocated fragment: the list only fetches the fields it renders.
const AuthClientsPage_AuthClient = graphql(`
  fragment AuthClientsPage_AuthClient on AuthClient {
    clientId
    clientName
    tokenEndpointAuthMethod
    grantTypes
    redirectUris
  }
`);

const AuthClientsPageQuery = graphql(`
  query AuthClientsPage_Query {
    authClients {
      ...AuthClientsPage_AuthClient
    }
  }
`);

const AuthClientsPage_DeleteAuthClients = graphql(`
  mutation AuthClientsPage_DeleteAuthClients($clientId: ID!) {
    deleteAuthClients(where: { clientId: $clientId }) {
      nodesDeleted
    }
  }
`);

export function AuthClientsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const [{ data }] = useQuery({ query: AuthClientsPageQuery });
  const [, deleteClient] = useMutation(AuthClientsPage_DeleteAuthClients);
  const { confirm, ConfirmationDialog } = useConfirm();

  const clients = getFragmentData(AuthClientsPage_AuthClient, data?.authClients ?? []);
  type ClientRow = (typeof clients)[number];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return clients;
    return clients.filter(
      (c) =>
        (c.clientName ?? "").toLowerCase().includes(s) ||
        (c.clientId ?? "").toLowerCase().includes(s),
    );
  }, [clients, q]);

  const onDelete = async (client: ClientRow) => {
    const ok = await confirm({
      title: "OAuth-Client löschen",
      message: `„${client.clientName ?? client.clientId}" wird entfernt. Anmeldungen über diesen Client schlagen danach fehl.`,
      confirmText: "Löschen",
      cancelText: "Abbrechen",
    });
    if (!ok) {
      return;
    }
    const res = await deleteClient({ clientId: client.clientId });
    if (res.error) {
      showUserErrorToast({ text: "Löschen fehlgeschlagen", error: res.error });
      return;
    }
    showSuccessToast("OAuth-Client gelöscht");
  };

  const columns: TableColumns<ClientRow>[] = [
    {
      id: "name",
      header: "Client Name",
      accessorKey: "clientName",
      size: 280,
      cell: ({ row }) => <span>{row.original.clientName}</span>,
    },
    {
      id: "clientId",
      header: "ClientId",
      accessorKey: "clientId",
      size: 220,
      cell: ({ row }) => (
        <code
          style={{
            background: "var(--color-grey-lightest)",
            padding: "2px 7px",
            borderRadius: 5,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 12,
          }}
        >
          {row.original.clientId}
        </code>
      ),
    },
    {
      id: "type",
      header: "Typ",
      accessorFn: (c) => (c.tokenEndpointAuthMethod === "none" ? "Public" : "Confidential"),
      size: 140,
      cell: ({ row }) => (
        <Chip
          value={row.original.tokenEndpointAuthMethod === "none" ? "Public" : "Confidential"}
          isSmall
        />
      ),
    },
    {
      id: "grants",
      header: "Grant Types",
      accessorFn: (c) => (c.grantTypes ?? []).join(", "),
      size: 240,
      cell: ({ row }) => (
        <span style={{ fontSize: 13, color: "var(--color-grey)" }}>
          {(row.original.grantTypes ?? []).join(", ") || "—"}
        </span>
      ),
    },
    {
      id: "redirect",
      header: "Redirect URIs",
      accessorFn: (c) => (c.redirectUris ?? [])[0] ?? "",
      size: 280,
      cell: ({ row }) => {
        const uris = row.original.redirectUris ?? [];
        return (
          <span style={{ fontSize: 13, color: "var(--color-grey)" }}>
            {uris[0] || "—"}
            {uris.length > 1 ? ` +${uris.length - 1}` : ""}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      size: 56,
      cell: ({ row }) => (
        <Dropdown
          trigger={
            <Button
              hierarchy="tertiary"
              icon={<DotsHorizontalIcon />}
              ariaLabel="Aktionen"
              onClick={(e) => e.stopPropagation()}
            />
          }
        >
          <DropdownMenu
            data={[
              {
                label: "Details öffnen",
                onClick: () => navigate(`/auth-clients/${row.original.clientId}`),
              },
              {
                label: "Löschen",
                color: "error",
                onClick: () => void onDelete(row.original),
              },
            ]}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <Page
      title="OAuth-Clients"
      description="OIDC-/OAuth-Registrierungen. Public Clients haben kein Secret."
      actions={[
        {
          text: "Client erstellen",
          icon: <AddIcon />,
          hierarchy: "primary",
          callback: () => setAdding(true),
        },
      ]}
    >
      <PageToolbar>
        <SearchInput
          name="client-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name oder Client-ID …"
        />
      </PageToolbar>
      <Table<ClientRow>
        columns={columns}
        data={filtered}
        isDesktop
        showSort
        showRowHover
        showShadow
        showBorderRadius
        onRowClick={(_, row) => navigate(`/auth-clients/${row.original.clientId}`)}
      />
      {adding && (
        <ClientFormModal
          mode="create"
          onClose={() => setAdding(false)}
          onSaved={(id) => {
            setAdding(false);
            navigate(`/auth-clients/${id}`);
          }}
        />
      )}
      <ConfirmationDialog />
    </Page>
  );
}
